/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useLocation, Link } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { Box, Row, Col, Tag, Heading, Breadcrumbs } from "@nordcloud/gnui";
import { trimText } from "~/tools";

const normalize = (value: string | undefined) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : "";

type TagsProps = {
  color: string;
  text: string;
};

type ComponentLabel = {
  value: string;
  label: string;
};

type ComponentLabelProps = {
  label: string;
  uri: string;
};

type BreadcrumbsBoxProps = {
  title?: string;
  tags?: TagsProps[];
  children?: React.ReactNode;
  labels?: ComponentLabel[];
  customBreadcrumbs?: ComponentLabelProps[];
};

export function BreadcrumbsBox({
  title,
  children,
  tags = [],
  labels,
  customBreadcrumbs,
}: BreadcrumbsBoxProps) {
  const location = useLocation();

  const path: string[] = location.pathname.split("/");

  const uniquePath: string[] = [...new Set(path.map((item: string) => item))];
  const displayTitle: string | undefined = title || uniquePath.at(-1);
  const START_PAGE_LABEL = "Home";
  const breadcrumbs = uniquePath.map((item: string, index: number) => {
    const label =
      item === ""
        ? trimText(START_PAGE_LABEL, 25)
        : trimText(
            labels?.find((element) => element.value === item)?.label || item,
            30
          );
    const uri = `${uniquePath.slice(0, index).join("/")}/${item}`;

    return { label, uri };
  });

  // TODO: add modifiers in GNUI and remove styles
  const inlineFlexStyles = { display: "inline-flex", placeItems: "center" };
  const flexStyles = { display: "flex", placeItems: "center" };

  return (
    <Box spacing="spacing04" data-testid="breadcrumbs-box">
      <Row style={flexStyles}>
        <Col>
          <div style={inlineFlexStyles}>
            <Heading level={2} css={{ wordBreak: "break-all" }}>
              {title ? displayTitle || "" : normalize(displayTitle)}
            </Heading>
            {tags.map((tag) => (
              <Tag
                key={uuidv4()}
                css={{ margin: "0 0 0 1rem" }}
                // @ts-expect-error The color prop should match required type
                color={tag.color}
                text={tag.text}
              />
            ))}
          </div>
          {customBreadcrumbs ? (
            <Breadcrumbs list={customBreadcrumbs} Component={Link} />
          ) : (
            <Breadcrumbs list={breadcrumbs} Component={Link} />
          )}
        </Col>
        {children && (
          <Col style={{ textAlign: "right" }}>
            <div style={{ display: "flex", marginLeft: "auto" }}>
              {children}
            </div>
          </Col>
        )}
      </Row>
    </Box>
  );
}
